import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import trainingApi from '../../services/trainingApi';

const ReflectionPrompt = ({ label, hint, value, onChange }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Typography variant="subtitle1" fontWeight={600} gutterBottom>{label}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      {hint}
    </Typography>
    <TextField
      multiline
      fullWidth
      minRows={3}
      maxRows={8}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Type your reflection..."
      variant="outlined"
    />
  </Paper>
);

const SelfReflectionPage = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();

  const [reflection, setReflection] = useState({
    whatWentWell: '',
    whatToDoDifferently: '',
    uncertainties: '',
  });
  const [session, setSession] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load session minimally so we can adapt copy/nav for therapist_practice mode.
  React.useEffect(() => {
    let cancelled = false;
    trainingApi.getSession(sessionId)
      .then(response => {
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        setSession(data.session || data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId]);

  const isTherapistPractice = session?.mode === 'therapist_practice';

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await trainingApi.submitReflection(sessionId, reflection);
      navigate(`/training/session/${sessionId}/feedback`);
    } catch (err) {
      setError(err.message || 'Failed to submit reflection');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    navigate(`/training/session/${sessionId}/feedback`);
  };

  const hasContent = reflection.whatWentWell || reflection.whatToDoDifferently || reflection.uncertainties;

  // Copy switches between trainee-flavoured and CPD-flavoured framing.
  const copy = isTherapistPractice ? {
    title: 'Reflective practice',
    subtitle: "A few minutes on what stood out in this session — what worked, what you'd revisit, and where you want to go deeper.",
    wellLabel: 'What worked well',
    wellHint: 'Rapport, line of questioning, formulation moves, decisions in the room.',
    differentlyLabel: "What you'd do differently next time",
    differentlyHint: 'Timing, framing, specific moments where the conversation drifted or stalled.',
    uncertaintiesLabel: 'Anything you want to revisit or read into',
    uncertaintiesHint: 'Knowledge edges, alternate formulations, things to bring to peer supervision.',
    submit: 'Save reflection',
  } : {
    title: 'Self-Reflection',
    subtitle: 'Take a moment to reflect on your performance in this session. Honest self-assessment is a key clinical skill.',
    wellLabel: 'What went well in this interview?',
    wellHint: 'Consider your rapport building, questioning technique, and clinical reasoning.',
    differentlyLabel: 'What would you do differently?',
    differentlyHint: 'Think about timing, question phrasing, areas you missed, or moments where you felt stuck.',
    uncertaintiesLabel: 'Were there moments where you felt uncertain? What would you want to learn more about?',
    uncertaintiesHint: 'Identify knowledge gaps and areas for further study.',
    submit: 'Submit Reflection',
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', py: 3 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="h5" gutterBottom>{copy.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {copy.subtitle}
      </Typography>

      <ReflectionPrompt
        label={copy.wellLabel}
        hint={copy.wellHint}
        value={reflection.whatWentWell}
        onChange={(val) => setReflection(prev => ({ ...prev, whatWentWell: val }))}
      />

      <ReflectionPrompt
        label={copy.differentlyLabel}
        hint={copy.differentlyHint}
        value={reflection.whatToDoDifferently}
        onChange={(val) => setReflection(prev => ({ ...prev, whatToDoDifferently: val }))}
      />

      <ReflectionPrompt
        label={copy.uncertaintiesLabel}
        hint={copy.uncertaintiesHint}
        value={reflection.uncertainties}
        onChange={(val) => setReflection(prev => ({ ...prev, uncertainties: val }))}
      />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="outlined" onClick={handleSkip}>
          Skip for now
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !hasContent}
        >
          {isSubmitting ? <CircularProgress size={20} /> : copy.submit}
        </Button>
      </Box>
    </Box>
  );
};

export default SelfReflectionPage;
